// src/pages/sales/CustomerForm.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Link as MuiLink,
  Stack,
  useTheme,
  alpha,
  Fade
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Home as HomeIcon,
  People as PeopleIcon,
  MonetizationOn as MonetizationOnIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';

const CustomerForm = ({ currentCompany }) => {
  const { companyId, customerId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isEditMode = !!customerId;
  const { hasPermission } = usePermissions();
  const [companyName, setCompanyName] = useState('');

  const [customer, setCustomer] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    payment_terms: '',
    customer_since: isEditMode ? null : dayjs()
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validation, setValidation] = useState({});

  useEffect(() => {
    if (isEditMode) {
      fetchCustomer();
    }
    fetchCompanyData();
  }, [customerId]);

  const fetchCustomer = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('id', customerId)
        .eq('company_id', companyId);
      
      if (error) throw error;
      
      if (!data || data.length === 0) {
        throw new Error('Customer not found');
      }
      
      if (data.length > 1) {
        console.warn('Multiple customers found with same ID, using first one');
      }
      
      const customerData = { ...data[0] };
      if (customerData.customer_since) {
        customerData.customer_since = dayjs(customerData.customer_since);
      }
      setCustomer(customerData);
    } catch (err) {
      console.error('Error fetching customer:', err);
      setError(`Failed to load customer: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyData = async () => {
    try {
      if (currentCompany && currentCompany.name) {
        setCompanyName(currentCompany.name);
        return;
      }

      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();

      if (error) throw error;

      if (data) {
        setCompanyName(data.name);
      }
    } catch (err) {
      console.error('Error fetching company data:', err);
      setCompanyName('Company Name');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomer(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (validation[name]) {
      setValidation(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleDateChange = (name, newValue) => {
    setCustomer(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    if (validation[name]) {
      setValidation(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!customer.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (customer.email && !/\S+@\S+\.\S+/.test(customer.email)) {
      errors.email = 'Invalid email format';
    }
    
    setValidation(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Check permissions before proceeding
    if (!hasPermission('sales.customers.edit', 'write')) {
      setError('You do not have permission to edit customers.');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      const customerData = {
        name: customer.name.trim(),
        company: customer.company ? customer.company.trim() : null,
        email: customer.email ? customer.email.trim() : null,
        phone: customer.phone ? customer.phone.trim() : null,
        street: customer.street ? customer.street.trim() : null,
        city: customer.city ? customer.city.trim() : null,
        state: customer.state ? customer.state.trim() : null,
        zip_code: customer.zip_code ? customer.zip_code.trim() : null,
        country: customer.country ? customer.country.trim() : null,
        payment_terms: customer.payment_terms ? customer.payment_terms.trim() : null,
        customer_since: customer.customer_since ? customer.customer_since.format('YYYY-MM-DD') : null,
        company_id: companyId
      };
      
      let result;
      
      if (isEditMode) {
        const { data, error } = await supabase
          .from('customers')
          .update(customerData)
          .eq('id', customerId)
          .select();
        
        if (error) throw error;
        result = data?.[0];
      } else {
        const { data, error } = await supabase
          .from('customers')
          .insert(customerData)
          .select();
        
        if (error) throw error;
        result = data?.[0];
      }
      
      navigate(`/company/${companyId}/customers-list`);
    } catch (err) {
      console.error('Error saving customer:', err);
      setError(`Failed to save customer: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // COMPANY OWNER NOTE: Company owners/creators automatically have ALL permissions
  // To test permissions properly, use a different user account with limited role
  
  // Permission check
  if (!hasPermission('sales.customers.view')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{ 
            p: 8, 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <PersonIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Customer Form Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access customer forms. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.history.back()}
              sx={{
                px: 6,
                py: 2,
                borderRadius: 3,
                fontWeight: 500,
                fontSize: '1rem',
                backgroundColor: 'primary.main',
                color: 'white',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.25)}`,
                '&:hover': { 
                  backgroundColor: 'primary.dark',
                  transform: 'translateY(-3px)',
                  boxShadow: `0 12px 36px ${alpha(theme.palette.primary.main, 0.35)}`
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }
  
  // Check edit permission for new/edit mode - both create and edit require write permission
  if (!hasPermission('sales.customers.edit', 'write')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{ 
            p: 8, 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <PersonIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Edit Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Customer Edit Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to create or edit customers. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.history.back()}
              sx={{
                px: 6,
                py: 2,
                borderRadius: 3,
                fontWeight: 500,
                fontSize: '1rem',
                backgroundColor: 'primary.main',
                color: 'white',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.25)}`,
                '&:hover': { 
                  backgroundColor: 'primary.dark',
                  transform: 'translateY(-3px)',
                  boxShadow: `0 12px 36px ${alpha(theme.palette.primary.main, 0.35)}`
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={48} sx={{ color: '#2196F3' }} />
      </Box>
    );
  }

  return (
    <Fade in timeout={800}>
      <Container 
        maxWidth="xl"
        sx={{
          py: 3,
          pb: 8,
          px: { xs: 2, sm: 3 },
          width: '100%',
          maxWidth: '100vw !important'
        }}
      >
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        {/* Enhanced Page Header */}
        <Card sx={{
          borderRadius: 3,
          mb: 3,
          mt: 5,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: theme.palette.grey[100],
          backdropFilter: 'blur(20px)',
          boxShadow: 'none',
          borderTop: '3px solid #2196F3',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 6px 20px ${alpha('#2196F3', 0.15)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
              <Box sx={{
                p: 2,
                borderRadius: 3,
                background: alpha('#2196F3', 0.08),
                border: `1px solid ${alpha('#2196F3', 0.15)}`,
              }}>
                <PersonIcon sx={{ fontSize: 32, color: '#2196F3' }} />
              </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{
                fontWeight: 500,
                color: '#2196F3',
                mb: 0.5,
                fontSize: '2rem',
                letterSpacing: '-0.5px'
              }}>
                {isEditMode ? 'Edit Customer' : 'New Customer'}
              </Typography>
              <Typography variant="body1" sx={{ 
                color: 'text.secondary', 
                fontWeight: 500,
                fontSize: '1rem'
              }}>
                {companyName || 'Loading...'}
              </Typography>
            </Box>
            <Button
              type="submit"
              form="customer-form"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saving}
              size="large"
              sx={{ 
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontSize: '0.9rem',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px) scale(1.02)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                }
              }}
            >
              {saving ? 'Saving...' : 'Save Customer'}
            </Button>
          </Box>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary', 
              maxWidth: 700, 
              lineHeight: 1.5,
              fontSize: '0.95rem',
              fontWeight: 500
            }}>
              {isEditMode ? 'Update customer information and contact details with comprehensive address management.' : 'Add a new customer to your database with their contact information and address details.'}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Customer Form */}
      <Paper sx={{ 
        borderRadius: 3, 
        overflow: 'hidden', 
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '@keyframes slideInUp': {
          '0%': {
            transform: 'translateY(100%)',
            opacity: 0,
          },
          '100%': {
            transform: 'translateY(0)',
            opacity: 1,
          }
        },
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }
      }}>
        <Box sx={{ p: 3 }}>
          <form id="customer-form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                    p: 2,
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                  }}
                >
                  <PersonIcon 
                    sx={{ 
                      mr: 2, 
                      fontSize: 28,
                      color: 'primary.main'
                    }} 
                  />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 500,
                      color: 'primary.main',
                      fontSize: '1.1rem'
                    }}
                  >
                    Customer Information
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="name"
                  label="Customer Name"
                  fullWidth
                  required
                  value={customer.name}
                  onChange={handleInputChange}
                  error={!!validation.name}
                  helperText={validation.name}
                  disabled={saving}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiInputBase-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      fontSize: '0.9rem',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      transform: 'translate(14px, 1px) scale(1)',
                      '&.Mui-focused': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      },
                      '&.MuiFormLabel-filled': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="company"
                  label="Company"
                  fullWidth
                  value={customer.company || ''}
                  onChange={handleInputChange}
                  disabled={saving}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiInputBase-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      fontSize: '0.9rem',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      transform: 'translate(14px, 1px) scale(1)',
                      '&.Mui-focused': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      },
                      '&.MuiFormLabel-filled': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="email"
                  label="Email"
                  fullWidth
                  value={customer.email || ''}
                  onChange={handleInputChange}
                  error={!!validation.email}
                  helperText={validation.email}
                  disabled={saving}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiInputBase-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      fontSize: '0.9rem',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      transform: 'translate(14px, 1px) scale(1)',
                      '&.Mui-focused': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      },
                      '&.MuiFormLabel-filled': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="phone"
                  label="Phone Number"
                  fullWidth
                  value={customer.phone || ''}
                  onChange={handleInputChange}
                  disabled={saving}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiInputBase-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      fontSize: '0.9rem',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      transform: 'translate(14px, 1px) scale(1)',
                      '&.Mui-focused': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      },
                      '&.MuiFormLabel-filled': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Customer Since"
                    value={customer.customer_since}
                    onChange={(newValue) => handleDateChange('customer_since', newValue)}
                    disabled={saving}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'outlined',
                        InputLabelProps: { shrink: true },
                        sx: {
                          '& .MuiInputBase-root': {
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            background: 'white',
                            fontSize: '0.9rem',
                            '& fieldset': { border: 'none' },
                            '&:hover': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                            },
                            '&.Mui-focused': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                            }
                          },
                          '& .MuiInputBase-input': {
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            transform: 'translate(14px, 1px) scale(1)',
                            '&.Mui-focused': {
                              transform: 'translate(14px, -17px) scale(0.75)'
                            },
                            '&.MuiFormLabel-filled': {
                              transform: 'translate(14px, -17px) scale(0.75)'
                            }
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  name="street"
                  label="Street Address"
                  fullWidth
                  value={customer.street || ''}
                  onChange={handleInputChange}
                  disabled={saving}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiInputBase-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      fontSize: '0.9rem',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      transform: 'translate(14px, 1px) scale(1)',
                      '&.Mui-focused': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      },
                      '&.MuiFormLabel-filled': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="city"
                  label="City"
                  fullWidth
                  value={customer.city || ''}
                  onChange={handleInputChange}
                  disabled={saving}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiInputBase-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      fontSize: '0.9rem',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      transform: 'translate(14px, 1px) scale(1)',
                      '&.Mui-focused': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      },
                      '&.MuiFormLabel-filled': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="state"
                  label="State/Province"
                  fullWidth
                  value={customer.state || ''}
                  onChange={handleInputChange}
                  disabled={saving}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiInputBase-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      fontSize: '0.9rem',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      transform: 'translate(14px, 1px) scale(1)',
                      '&.Mui-focused': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      },
                      '&.MuiFormLabel-filled': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="zip_code"
                  label="ZIP/Postal Code"
                  fullWidth
                  value={customer.zip_code || ''}
                  onChange={handleInputChange}
                  disabled={saving}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiInputBase-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      fontSize: '0.9rem',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      transform: 'translate(14px, 1px) scale(1)',
                      '&.Mui-focused': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      },
                      '&.MuiFormLabel-filled': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="country"
                  label="Country"
                  fullWidth
                  value={customer.country || ''}
                  onChange={handleInputChange}
                  disabled={saving}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiInputBase-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      fontSize: '0.9rem',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      transform: 'translate(14px, 1px) scale(1)',
                      '&.Mui-focused': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      },
                      '&.MuiFormLabel-filled': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  name="payment_terms"
                  label="Payment Terms"
                  fullWidth
                  value={customer.payment_terms || ''}
                  onChange={handleInputChange}
                  disabled={saving}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiInputBase-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      fontSize: '0.9rem',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      transform: 'translate(14px, 1px) scale(1)',
                      '&.Mui-focused': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      },
                      '&.MuiFormLabel-filled': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ mb: 3, mt: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/company/${companyId}/customers-list`)}
                    disabled={saving}
                    sx={{
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      color: 'text.primary',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.grey[100], 0.8),
                        border: `1px solid ${theme.palette.primary.main}`,
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Paper>
        </Container>
    </Fade>
  );
};

export default CustomerForm;